import { addDays } from '../../../library/dateTimeHelper';
import { DIARY_START_STEP_MINUTES } from '../../../library/freeSlotTimes';
import { AppointmentStatus, IAppointment, occupiesTime } from '../../../library/models/appointment';
import { IClinicProgram, IProgramDay, weekdayOf } from '../../../library/models/clinicProgram';
import ResourcesIds from '../../../resources/ResourcesIds';
import type { AppointmentTone } from './myCalendar.styles';

/**
 * The fallback axis, used only when the clinic's program has not loaded yet or has no open
 * day at all.
 *
 * These used to *be* the clinic's hours. They are not any more -- a clinic says when it is
 * open day by day, and `hourBoundsFor` reads that. They stay as the answer for the moment
 * before the program arrives, because an axis has to be something and a grid one hour tall
 * is worse than a grid on the wrong hours.
 *
 * The grid stretches beyond whatever it starts from when something falls outside it rather
 * than clipping it -- a session that ran late is exactly what somebody scrolls back to find,
 * and a view that silently omitted it would be worse than no view at all.
 */
export const DEFAULT_FIRST_HOUR = 8;
export const DEFAULT_LAST_HOUR = 19;

/**
 * A declined request is drawn as a cancelled booking: it gave its hour back the same way, and
 * it only reaches the grid at all with *Show cancelled* on. Its label still says which it was.
 */
export const TONES: Record<AppointmentStatus, AppointmentTone> = {
    SCHEDULED: 'scheduled',
    HELD: 'held',
    NO_SHOW: 'noShow',
    CANCELLED: 'cancelled',
    REQUESTED: 'requested',
    DECLINED: 'cancelled',
};

export const STATUS_LABELS: Record<AppointmentStatus, string> = {
    SCHEDULED: ResourcesIds.APPOINTMENT_STATUS_SCHEDULED,
    HELD: ResourcesIds.APPOINTMENT_STATUS_HELD,
    NO_SHOW: ResourcesIds.APPOINTMENT_STATUS_NO_SHOW,
    CANCELLED: ResourcesIds.APPOINTMENT_STATUS_CANCELLED,
    REQUESTED: ResourcesIds.APPOINTMENT_STATUS_REQUESTED,
    DECLINED: ResourcesIds.APPOINTMENT_STATUS_DECLINED,
};

/** Minutes since midnight, read straight off the wall-clock string. */
export const minutesInto = (wallClock: string): number => {
    const [, time = '00:00:00'] = wallClock.split('T');
    const [hours, minutes] = time.split(':').map(Number);

    return hours * 60 + minutes;
};

export const clockOf = (wallClock: string): string => wallClock.split('T')[1]?.slice(0, 5) ?? '';

const DAY_MINUTES = 24 * 60;

const startDayOf = (appointment: IAppointment): string => appointment.startsAt.slice(0, 10);

const endsAtMidnight = (appointment: IAppointment): boolean => clockOf(appointment.endsAt) === '00:00';

/**
 * The last day an entry occupies any of. An end at midnight belongs to the day before it:
 * a block until Tuesday 00:00 is Monday's, and Tuesday is free.
 */
const lastDayOf = (appointment: IAppointment): string => {
    const endDay: string = appointment.endsAt.slice(0, 10);

    return endsAtMidnight(appointment) && endDay > startDayOf(appointment)
        ? addDays(endDay, -1)
        : endDay;
};

/** Whether an entry holds any of a day. A block of leave holds every day from its first to its last. */
export const occupiesDay = (appointment: IAppointment, day: string): boolean =>
    startDayOf(appointment) <= day && day <= lastDayOf(appointment);

/**
 * The minutes since midnight an entry occupies on one day, from 0 to 1440.
 *
 * The question every drawing of the diary used to answer with `minutesInto` alone, which reads
 * the clock and ignores the date -- right for as long as nothing crossed midnight. A block of
 * leave from Friday 14:00 to Tuesday 10:00 is 14:00 to 24:00 on Friday, the whole of Saturday
 * to Monday, and 00:00 to 10:00 on Tuesday. Only meaningful for a day it `occupiesDay`.
 */
export const spanOn = (appointment: IAppointment, day: string): { from: number; to: number } => ({
    from: startDayOf(appointment) < day ? 0 : minutesInto(appointment.startsAt),
    to: appointment.endsAt.slice(0, 10) > day ? DAY_MINUTES : minutesInto(appointment.endsAt),
});

/**
 * Where a card is drawn on a column, as minutes since midnight: its span on that day, held
 * inside the axis.
 *
 * The axis is widened for every edge an entry really has -- see `hourBoundsFor` -- so this
 * only ever trims the part of a multi-day block that runs on past the clinic's hours, which
 * is what keeps a week of leave from stretching every column to midnight. A piece lying
 * wholly outside the axis, the small hours of a block that began late the evening before,
 * is drawn as one step at the edge it is beyond, so it can still be seen and opened.
 */
export const placeOn = (
    appointment: IAppointment,
    day: string,
    firstHour: number,
    lastHour: number
): { top: number; bottom: number } => {
    const { from, to } = spanOn(appointment, day);
    const axisStart: number = firstHour * 60;
    const axisEnd: number = Math.max(lastHour, firstHour + 1) * 60;

    if (to <= axisStart) {
        return { top: axisStart, bottom: axisStart + DIARY_START_STEP_MINUTES };
    }

    if (from >= axisEnd) {
        return { top: axisEnd - DIARY_START_STEP_MINUTES, bottom: axisEnd };
    }

    return { top: Math.max(from, axisStart), bottom: Math.min(to, axisEnd) };
};

/**
 * The moments an entry needs the axis to reach, as minutes on the days it has them.
 *
 * Its start, on the day it starts, and its end, on its last day -- never the edges of a day
 * it merely continues through, which are nothing but midnight. And not a midnight edge of
 * something that runs for a day or more either: leave from Friday 00:00 is leave for the
 * whole of Friday, and stretching the week's axis to midnight to say so would squash every
 * working hour on the screen. An entry inside one day gives both its edges, exactly as
 * before; one spanning days gives each real edge with a step beside it, so a block starting
 * at 23:30 still has room to be drawn.
 */
const axisEdgesOf = (appointment: IAppointment, days?: string[]): number[] => {
    const startDay: string = startDayOf(appointment);
    const lastDay: string = lastDayOf(appointment);
    const startsAtMidnight: boolean = minutesInto(appointment.startsAt) === 0;
    const spansADay: boolean = startDay < lastDay || (startsAtMidnight && endsAtMidnight(appointment));
    const inView = (day: string): boolean => !days || days.includes(day);

    const start: number | undefined = inView(startDay) && !(spansADay && startsAtMidnight)
        ? minutesInto(appointment.startsAt)
        : undefined;
    const end: number | undefined = inView(lastDay) && !(spansADay && endsAtMidnight(appointment))
        ? spanOn(appointment, lastDay).to
        : undefined;

    if (startDay === lastDay && start !== undefined && end !== undefined) {
        return [start, end];
    }

    return [
        ...(start === undefined ? [] : [start, Math.min(start + DIARY_START_STEP_MINUTES, DAY_MINUTES)]),
        ...(end === undefined ? [] : [Math.max(end - DIARY_START_STEP_MINUTES, 0), end]),
    ];
};

/** `HH:mm` of a minute since midnight -- the inverse of `minutesInto`. */
export const clockAtMinute = (minutes: number): string =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * The steps of the drawn axis that nothing occupies, as minutes since midnight.
 *
 * What the grid offers to book into. Occupancy is `occupiesTime`, the same
 * `status IN ('SCHEDULED','HELD')` the API's own overlap guard asks -- so a booking called
 * off, which is on the grid only while *Show cancelled* is on, does not hold on to the hour
 * it gave back, and a block of leave does hold on to its own.
 *
 * A step is dropped whenever anything at all reaches into it, rather than merely starting in
 * it: a session running to 09:20 leaves nothing bookable before half past, and offering 09:00
 * there would be offering a clash. Touching is not overlapping -- a step beginning exactly as
 * something ends is free -- which is the same strict comparison `layoutLanes` and the API use.
 *
 * The count of steps is derived from `Math.max(lastHour - firstHour, 1)`, which is what
 * `ScheduleGrid` draws, so the clickable layer is never taller or shorter than the grid.
 *
 * `day` is the column's own date, because an entry spanning days occupies different hours
 * on each of them -- see `spanOn`.
 */
export const freeStepsOn = (
    appointments: IAppointment[],
    firstHour: number,
    lastHour: number,
    stepMinutes: number,
    day: string
): number[] => {
    // Each entry's span on *this* day. Read off the clock alone, a block crossing midnight
    // came out as [1200, 120] and blocked nothing, and a block's later days were all free.
    const busy: number[][] = appointments
        .filter(occupiesTime)
        .map(appointment => {
            const { from, to } = spanOn(appointment, day);
            return [from, to];
        });

    const steps: number = Math.floor((Math.max(lastHour - firstHour, 1) * 60) / stepMinutes);

    return Array.from({ length: Math.max(steps, 0) }, (_, index) => firstHour * 60 + index * stepMinutes)
        .filter(start => busy.every(([from, to]) => start >= to || start + stepMinutes <= from));
};

/**
 * The hour axis every column shares.
 *
 * Computed across the whole visible range rather than per day, which matters the moment
 * there is more than one column: seven columns each with their own first hour would put
 * nine o'clock at a different height in each of them, and that is not a week view. So a
 * week whose days differ gets one axis spanning the widest of them, and a Wednesday that
 * shuts at two shows empty grid below it -- which is correct, because empty grid is where
 * the therapist has room.
 *
 * The program is optional, and without it this is what it always was. That is not only for
 * the moment before the query lands: `NewEntryDialog` and the tests that measure the grid
 * have no clinic in hand and should not have to acquire one.
 *
 * Order matters. The program sets the base, and the appointments then widen it -- never the
 * other way round, or an emergency a therapist booked on a Sunday the clinic is shut would
 * be off the grid entirely.
 *
 * What widens it is each entry's real edges -- see `axisEdgesOf` -- so a block of leave
 * spanning days does not drag every column's axis out to midnight.
 */
export const hourBoundsFor = (
    appointments: IAppointment[],
    program?: IClinicProgram,
    days?: string[]
): { firstHour: number; lastHour: number } => {
    const base = programBounds(program, days);
    const edges: number[] = appointments.flatMap(a => axisEdgesOf(a, days));

    return {
        firstHour: Math.min(base.firstHour, ...edges.map(minute => Math.floor(minute / 60))),
        lastHour: Math.max(base.lastHour, ...edges.map(minute => Math.ceil(minute / 60))),
    };
};

/**
 * The union of the open hours of the days on screen.
 *
 * Falls back twice over. A range in which every day is shut -- a therapist opening a Sunday,
 * or a clinic with a short week -- widens to the program's other days, so the axis is still
 * the clinic's own rather than a constant. A program with no open day at all falls back to
 * the constants, because an empty axis would collapse `ScheduleGrid`'s
 * `Math.max(lastHour - firstHour, 1)` to a single meaningless hour.
 */
const programBounds = (
    program?: IClinicProgram,
    days?: string[]
): { firstHour: number; lastHour: number } => {
    if (!program) {
        return { firstHour: DEFAULT_FIRST_HOUR, lastHour: DEFAULT_LAST_HOUR };
    }

    const onScreen: IProgramDay[] = (days ?? [])
        .map((day: string) => program.days.find(entry => entry.dayOfWeek === weekdayOf(day)))
        .filter((day): day is IProgramDay => !!day && day.open);

    const open: IProgramDay[] = onScreen.length
        ? onScreen
        : program.days.filter((day: IProgramDay) => day.open);

    if (!open.length) {
        return { firstHour: DEFAULT_FIRST_HOUR, lastHour: DEFAULT_LAST_HOUR };
    }

    return {
        firstHour: Math.min(...open.map(day => Math.floor(minutesIntoTime(day.opensAt) / 60))),
        lastHour: Math.max(...open.map(day => Math.ceil(minutesIntoTime(day.closesAt) / 60))),
    };
};

/** Minutes since midnight of a bare `HH:mm`, which is what the program carries. */
const minutesIntoTime = (clock: string): number => {
    const [hours, minutes] = clock.split(':').map(Number);

    return hours * 60 + minutes;
};

/**
 * One card's place across its column: which lane it sits in, and how many share the hours
 * it covers.
 */
export interface LanedAppointment {
    appointment: IAppointment;
    /** Zero-based, left to right. */
    lane: number;
    /** How many lanes the column is split into where this card sits. Never below 1. */
    lanes: number;
}

/**
 * Side-by-side placement for cards whose hours overlap.
 *
 * Two things a therapist looks at can occupy one hour. Two *live* ones cannot -- the API
 * refuses that for a single therapist -- but a cancelled booking and the replacement made
 * in the slot it gave back genuinely do, and so does a cancelled booking under a block of
 * leave. Drawn in one lane they landed exactly on top of each other, and the card on top
 * took every click: the one underneath could not be read, let alone reopened.
 *
 * Overlaps are found in clusters rather than pairwise, because the width has to be shared
 * by everything that meets: three cards across one hour are three lanes, not a two-lane
 * split with one of them doubled up. A cluster ends at the first appointment that starts
 * at or after everything so far has finished, and the next one starts fresh at full width.
 *
 * Touching is not overlapping -- an appointment ending exactly as the next begins is how a
 * clinic runs its day, and the same strict comparison the API's own overlap guard uses.
 *
 * Compared as whole wall-clock strings, which sort chronologically, so a session running
 * past midnight is still ordered correctly rather than wrapping to minute zero.
 */
export const layoutLanes = (appointments: IAppointment[]): LanedAppointment[] => {
    /*
     * Earliest first, and the longer of two starting together first -- so the card that
     * spans the cluster anchors the left lane and the short ones stack to its right, which
     * is the shape the eye expects. Ties broken by id, so the same day always lays out the
     * same way whatever order the API happened to answer in.
     */
    const inOrder: IAppointment[] = [...appointments].sort(
        (a, b) =>
            a.startsAt.localeCompare(b.startsAt) ||
            b.endsAt.localeCompare(a.endsAt) ||
            a.id - b.id
    );

    const laid: LanedAppointment[] = [];

    // The cluster being filled: what is in it, and when each of its lanes is next free.
    let cluster: LanedAppointment[] = [];
    let laneEnds: string[] = [];

    /** Every card in a cluster is as wide as the widest point of it, so they line up. */
    const closeCluster = (): void => {
        cluster.forEach(entry => laid.push({ ...entry, lanes: laneEnds.length }));
        cluster = [];
        laneEnds = [];
    };

    inOrder.forEach(appointment => {
        if (laneEnds.length && laneEnds.every(end => end <= appointment.startsAt)) {
            closeCluster();
        }

        // The leftmost lane that is free by the time this starts, or one of its own.
        const free: number = laneEnds.findIndex(end => end <= appointment.startsAt);
        const lane: number = free === -1 ? laneEnds.length : free;

        laneEnds[lane] =
            laneEnds[lane] && laneEnds[lane] > appointment.endsAt
                ? laneEnds[lane]
                : appointment.endsAt;

        cluster.push({ appointment, lane, lanes: 1 });
    });

    closeCluster();

    return laid;
};

/**
 * The appointments of a range, bucketed by day.
 *
 * Seeded with every day asked for, so a Tuesday nobody booked still gets an entry and the
 * grid still draws its column. An empty column is information -- it is where the therapist
 * has room.
 */
export const groupByDay = (
    appointments: IAppointment[],
    days: string[]
): Record<string, IAppointment[]> => {
    const byDay: Record<string, IAppointment[]> = Object.fromEntries(days.map(day => [day, []]));

    // Into every day it holds, not only the one it starts on: the Monday of a week's leave
    // is as much on Monday's column as its Friday is on Friday's.
    appointments.forEach(appointment => {
        days.filter(day => occupiesDay(appointment, day))
            .forEach(day => byDay[day].push(appointment));
    });

    return byDay;
};
